import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/AppLayout";
import FilterBar from "../components/FilterBar";
import ProductTable from "../components/ProductTable";
import { exportProductsExcel } from "../lib/exportProductsExcel";
import GridView from "../components/GridView";
import VariantFormDialog from "../components/VariantFormDialog";
import ProductDetailDialog from "../components/ProductDetailDialog";
import ProductViewsBar, { useProductViews } from "../components/ProductViewsBar";
import SimilarItemsDialog from "../components/SimilarItemsDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Table2, LayoutGrid, LayoutList, RefreshCw, ChevronDown, Link2, PackagePlus, Copy, Tag, FileOutput, Rows3, Rows2, Maximize2, Minimize2, FileSpreadsheet, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";

// iter 211 (2026-02): 会话级排序菜单。字段 key 与后端 SORTABLE_FIELDS
// 白名单一一对应;label 走 i18n,若翻译键缺失则回退到英文。
const SORT_OPTIONS = [
  { key: "product_id",     label_en: "Default (Product order)", label_zh: "默认 (录入顺序)" },
  { key: "item_no",        label_en: "Item No",                 label_zh: "产品编号" },
  { key: "description",    label_en: "Description",             label_zh: "产品描述" },
  { key: "contract_no",    label_en: "Contract No",             label_zh: "合同号" },
  { key: "contract_date",  label_en: "Contract Date",           label_zh: "合同日期" },
  { key: "shipping_date",  label_en: "Shipping Date",           label_zh: "出货日期" },
  { key: "available_date", label_en: "Available Date",          label_zh: "可售日期" },
  { key: "qty",            label_en: "Order Qty (sum)",         label_zh: "订单数量 (求和)" },
  { key: "price_order",    label_en: "Sales Price (max)",       label_zh: "销售价 (最大)" },
  { key: "cbm_total",      label_en: "CBM Total (sum)",         label_zh: "总体积 (求和)" },
];

function SortMenu({ sortKey, sortOrder, changeSort }) {
  const { i18n } = useTranslation();
  const isZh = (i18n.language || "en").toLowerCase().startsWith("zh");
  const current = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];
  const currentLabel = isZh ? current.label_zh : current.label_en;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="products-sort-trigger"
          className="h-7 px-2 rounded-md border border-gray-200 flex items-center gap-1 text-[11px] text-gray-700 hover:border-brand-300 bg-white transition-colors"
          title={isZh ? "排序" : "Sort"}
        >
          <ArrowUpDown size={11} strokeWidth={2} className="text-gray-400" />
          <span className="max-w-[120px] truncate">{currentLabel}</span>
          {sortKey !== "product_id" && (
            sortOrder === "asc"
              ? <ArrowUp size={10} strokeWidth={2.5} className="text-brand-600" />
              : <ArrowDown size={10} strokeWidth={2.5} className="text-brand-600" />
          )}
          <ChevronDown size={11} strokeWidth={2} className="text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="products-sort-menu">
        {SORT_OPTIONS.map((opt) => {
          const active = opt.key === sortKey;
          const label = isZh ? opt.label_zh : opt.label_en;
          if (opt.key === "product_id") {
            return (
              <DropdownMenuItem
                key={opt.key}
                data-testid={`products-sort-option-${opt.key}`}
                onSelect={() => changeSort(opt.key, "asc")}
                className={`text-xs ${active ? "bg-brand-50 text-brand-700 font-medium" : ""}`}
              >
                {label}
              </DropdownMenuItem>
            );
          }
          return (
            <div key={opt.key} className={`flex items-center px-1.5 py-1 rounded ${active ? "bg-brand-50" : ""}`}>
              <span className={`flex-1 text-xs truncate ${active ? "text-brand-700 font-medium" : "text-gray-700"}`}>{label}</span>
              <button
                data-testid={`products-sort-${opt.key}-asc`}
                onClick={() => changeSort(opt.key, "asc")}
                className={`w-6 h-6 flex items-center justify-center rounded ${active && sortOrder === "asc" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-100"}`}
                title={isZh ? "升序" : "Ascending"}
              >
                <ArrowUp size={11} strokeWidth={2.25} />
              </button>
              <button
                data-testid={`products-sort-${opt.key}-desc`}
                onClick={() => changeSort(opt.key, "desc")}
                className={`w-6 h-6 flex items-center justify-center rounded ${active && sortOrder === "desc" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-100"}`}
                title={isZh ? "降序" : "Descending"}
              >
                <ArrowDown size={11} strokeWidth={2.25} />
              </button>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// iter 209 (2026-02): customer_name filter removed from products page per
// user request. Kept the field on grouped products (line ~322) because other
// parts of the app (e.g., grid card view) still surface it visually.
const EMPTY_FILTERS = { q: "", min_qty: "", max_qty: "", min_shipping_qty: "", max_shipping_qty: "", min_ci_qty: "", max_ci_qty: "", has_samples: false, sample_warehouse_id: "", category: "", product_type: "", budget_no: "", factory_name: "", purchaser: "", external_sales: "", contract_no: "", invoice_no: "", tag: "", approval: "", date_field: "contract_date", date_from: "", date_to: "" };

export default function Dashboard() {
  const { t } = useTranslation();
  const { addToCart, activeCart } = useCart();
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const canEditMaster = user && ["admin", "ci_admin"].includes(user.role);  // iter 279
  // iter 178l: BH Product Admin can also generate public share links for
  // selected products (same UI entrypoint, same backend gate widened to
  // BH_ALLOWED_ROLES). Everything else remains admin-only.
  const canGenerateShareLink = user && (user.role === "admin" || user.role === "bh_product_admin");
  const [rows, setRows] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  // iter 180: server-side totals across the full filtered result set
  // (not just the current page). Consumed by ProductTable's bottom summary
  // row for qty / shipping_qty / amount columns.
  const [totals, setTotals] = useState({ qty: 0, shipping_qty: 0, amount: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [variantProduct, setVariantProduct] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("productsViewMode") || "table");
  const changeView = (v) => { setView(v); localStorage.setItem("productsViewMode", v); };
  // iter 198 (2026-02): 密度切换 — 紧凑视图 (默认) vs 舒适视图。紧凑:字号
  // 12px, 单元格 padding 极小, 完整网格边框, 无圆角外壳。用户偏好存 localStorage。
  const [density, setDensity] = useState(() => localStorage.getItem("productTableDensity") || "compact");
  const changeDensity = (d) => { setDensity(d); localStorage.setItem("productTableDensity", d); };
  // iter 203 (2026-02): 单击行高亮 (会话内,session-scoped)。点行任意空白
  // 处即选中,琥珀黄底突出。再点同一行取消;点另一行自动切换。不进 localStorage。
  const [selectedProductId, setSelectedProductId] = useState(null);
  // iter 207 (2026-02): 全屏模式 — 隐藏侧边栏 + 页头, table 占满 viewport。
  // 不用浏览器原生 Fullscreen API (会隐藏 tab / 地址栏,反而不熟悉),用 CSS
  // fixed 层。ESC 键退出。状态持久到 localStorage。
  const [tableFullscreen, setTableFullscreen] = useState(() => localStorage.getItem("productsTableFullscreen") === "1");
  const toggleFullscreen = () => setTableFullscreen((v) => {
    const next = !v;
    localStorage.setItem("productsTableFullscreen", next ? "1" : "0");
    return next;
  });
  useEffect(() => {
    if (!tableFullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") toggleFullscreen(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFullscreen]);
  // iter 123: compact grid packs 8+ cards per row so 20/page fits in less
  // than a scroll - bump it to 50 so users see more before paginating.
  // iter 169: user can now pick 25 / 50 / 100 explicitly (persisted). Default
  // varies by view: grid-compact = 50, others = 25.
  const [pageSize, setPageSize] = useState(() => {
    const stored = parseInt(localStorage.getItem("productsPageSize") || "", 10);
    return [25, 50, 100].includes(stored) ? stored : (view === "grid-compact" ? 50 : 25);
  });
  const changePageSize = (n) => { setPageSize(n); localStorage.setItem("productsPageSize", String(n)); setPage(1); };
  const PAGE_SIZE = pageSize;
  const [sortDir, setSortDir] = useState("desc");
  // iter 211 (2026-02): 顶部会话级排序控件。仅内存 + localStorage,不落盘
  // 到 View。sort_key 白名单见后端 SORTABLE_FIELDS;不合法值后端会自动
  // 回退到 product_id 排序。
  const [sortKey, setSortKey] = useState(() => localStorage.getItem("productsSortKey") || "product_id");
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("productsSortOrder") || "asc");
  const changeSort = useCallback((k, o) => {
    setSortKey(k);
    setSortOrder(o);
    localStorage.setItem("productsSortKey", k);
    localStorage.setItem("productsSortOrder", o);
    setPage(1);
  }, []);
  const [dataAsOf, setDataAsOf] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailIndex, setDetailIndex] = useState(0);
  const [priceCurrency, setPriceCurrency] = useState("USD");
  const [similarLookup, setSimilarLookup] = useState(null); // { itemNo, targetProduct }
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [flushingApprovals, setFlushingApprovals] = useState(false);
  // iter 123: selection state lives at Dashboard level so it survives
  // switching between List/GridCompact/GridDetailed AND paging (previously
  // it lived inside ProductTable and evaporated on every view change).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedListOpen, setSelectedListOpen] = useState(false);
  // Map of product_id -> {item_no, description, contract_no} kept across
  // pages so the "Show selected" dialog can still list a product that was
  // ticked on page 1 while user is on page 5.
  const [selectedMeta, setSelectedMeta] = useState({});
  const toggleSelectedId = useCallback((productId, row) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
    if (row) {
      setSelectedMeta((prev) => ({
        ...prev,
        [productId]: {
          product_id: productId,
          item_no: row.item_no,
          description: row.description,
          contract_no: row.contract_no,
        },
      }));
    }
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedMeta({});
  }, []);

  // iter 178: generate a public share link for the selected products
  // (admin only). Opens a dialog showing the URL + expiry, with a Copy
  // button so the user can share it without hunting for the address bar.
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const [shareLinkGenerating, setShareLinkGenerating] = useState(false);
  const [shareLinkInfo, setShareLinkInfo] = useState(null);
  const generateShareLink = useCallback(async () => {
    if (!canGenerateShareLink || selectedIds.size === 0) return;
    setShareLinkGenerating(true);
    try {
      const { data } = await api.post("/product-share-links", {
        product_ids: Array.from(selectedIds),
      });
      const url = `${window.location.origin}/share/products/${data.token}`;
      setShareLinkInfo({ ...data, url });
      setShareLinkDialogOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || t("toast.failed_generate_link"));
    } finally {
      setShareLinkGenerating(false);
    }
  }, [canGenerateShareLink, selectedIds, t]);
  const copyShareLink = useCallback(() => {
    if (!shareLinkInfo?.url) return;
    navigator.clipboard.writeText(shareLinkInfo.url).then(
      () => toast.success(t("toast.copied")),
      () => toast.error(t("toast.failed_copy")),
    );
  }, [shareLinkInfo, t]);

  // iter 178h: Set Tag / Print Label 从 ProductTable 内部黑条搬到 Dashboard
  // 的 Action 下拉。fetchRows/filters/page 依赖的 hooks 在下面才定义,所以
  // 我们只声明 state 在这;handleConfirmSetTag callback 在 fetchRows 之后定义。
  const [setTagOpen, setSetTagOpen] = useState(false);
  const [tagValue, setTagValue] = useState("");
  const [savingTag, setSavingTag] = useState(false);
  // iter 178n: user's own past tags surface as clickable chips inside the
  // Set Tag dialog for quick reuse. Fetched lazily when the dialog opens.
  const [myTags, setMyTags] = useState([]);
  useEffect(() => {
    if (!setTagOpen) return;
    let cancelled = false;
    api.get("/tags/my").then(({ data }) => {
      if (!cancelled) setMyTags(data.items || []);
    }).catch(() => { if (!cancelled) setMyTags([]); });
    return () => { cancelled = true; };
  }, [setTagOpen]);
  const handleBulkPrintLabel = useCallback(() => {
    toast.info("Print Label is coming soon — pending label format template");
  }, []);

  const productViews = useProductViews({ filters, setFilters, emptyFilters: EMPTY_FILTERS, sortDir, setSortDir, priceCurrency, setPriceCurrency });

  // iter 201: keep the latest qFields in a ref so fetchRows (memoized with
  // just [PAGE_SIZE]) can read the current value without recreation.
  const qFieldsRef = useRef(productViews.qFields);
  useEffect(() => { qFieldsRef.current = productViews.qFields; }, [productViews.qFields]);
  // iter 275: the "Sample Warehouse" view follows the active quotation's warehouse.
  const sampleWarehouseScope = productViews.currentView?.name === "Sample Warehouse" && activeCart?.sample_warehouse_id
    ? { id: activeCart.sample_warehouse_id, name: activeCart.sample_warehouse_name } : null;
  const activeCartIdRef = useRef(activeCart?.id);
  useEffect(() => { activeCartIdRef.current = activeCart?.id; }, [activeCart?.id]);
  const sampleWarehouseScopeRef = useRef(sampleWarehouseScope);
  useEffect(() => { sampleWarehouseScopeRef.current = sampleWarehouseScope; }, [sampleWarehouseScope]);

  // Track in-flight inventory request so filter/page changes can cancel stale
  // ones (2026-08-30, iter 76). Without this a slow response arriving after a
  // newer one would clobber the fresh rows on screen.
  const inflightRef = useRef(null);
  const fetchRows = useCallback(async (f, targetPage = 1) => {
    if (inflightRef.current) inflightRef.current.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    setLoading(true);
    try {
      const params = { page: targetPage, page_size: PAGE_SIZE };
      if (f.q) params.q = f.q;
      // iter 201: q_fields limits which fields the keyword search covers,
      // configured per View. When empty, backend falls back to legacy set.
      const currentQFields = qFieldsRef.current;
      if (f.q && Array.isArray(currentQFields) && currentQFields.length) {
        params.q_fields = currentQFields.join(",");
      }
      if (f.min_qty !== "") params.min_qty = f.min_qty;
      if (f.max_qty !== "") params.max_qty = f.max_qty;
      // iter 213 (2026-02): 出货数量范围
      if (f.min_shipping_qty !== "" && f.min_shipping_qty != null) params.min_shipping_qty = f.min_shipping_qty;
      if (f.max_shipping_qty !== "" && f.max_shipping_qty != null) params.max_shipping_qty = f.max_shipping_qty;
      if (f.min_ci_qty !== "" && f.min_ci_qty != null) params.min_ci_qty = f.min_ci_qty;
      if (f.max_ci_qty !== "" && f.max_ci_qty != null) params.max_ci_qty = f.max_ci_qty;
      if (f.has_samples) params.has_samples = true;
      if (activeCartIdRef.current) params.active_cart_id = activeCartIdRef.current;  // iter 277
      if (f.sample_warehouse_id) params.sample_warehouse_id = f.sample_warehouse_id;
      else if (sampleWarehouseScopeRef.current) params.sample_warehouse_id = sampleWarehouseScopeRef.current.id;
      if (f.category) params.category = f.category;
      if (f.product_type) params.product_type = f.product_type;
      // iter 209: customer_name filter removed — 相关 params 也不再发送。
      if (f.budget_no) params.budget_no = f.budget_no;
      if (f.factory_name) params.factory_name = f.factory_name;
      if (f.purchaser) params.purchaser = f.purchaser;
      if (f.external_sales) params.external_sales = f.external_sales;
      if (f.contract_no) params.contract_no = f.contract_no;
      if (f.invoice_no) params.invoice_no = f.invoice_no;
      if (f.tag) params.tag = f.tag;
      if (f.approval) params.approval = f.approval;
      if (f.date_from || f.date_to) {
        params.date_field = f.date_field || "contract_date";
        if (f.date_from) params.date_from = f.date_from;
        if (f.date_to) params.date_to = f.date_to;
      }
      // iter 211 (2026-02): 会话级排序参数。sort_key = "product_id" (默认)
      // 时不发,减少 URL 噪音;后端见空值也会 fallback 到默认。
      if (sortKey && sortKey !== "product_id") {
        params.sort_by = sortKey;
        params.sort_dir = sortOrder;
      }
      const { data } = await api.get("/inventory", { params, signal: controller.signal });
      if (controller.signal.aborted) return;
      setRows(data.items || []);
      setTotalProducts(data.total || 0);
      setTotals(data.totals || { qty: 0, shipping_qty: 0, amount: 0 });
      setPage(data.page || targetPage);
      setDataAsOf(new Date().toLocaleString("en-US"));
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("toast.failed_load"));
    } finally {
      if (inflightRef.current === controller) {
        inflightRef.current = null;
        setLoading(false);
      }
    }
  }, [PAGE_SIZE, sortKey, sortOrder]);

  useEffect(() => {
    api.get("/inventory/meta").then(({ data }) => setCategoryOptions(data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    // Filter changes reset to page 1; page changes only re-fetch (below).
    // iter 117: debounce lifted 150 → 400 ms. At 150 ms every keystroke on a
    // fast typist fires a separate fetch, floods the server, and the mid-word
    // requests waste round trips (server-side aggregate against 300+ product
    // union is ~150-400 ms). 400 ms lets typing settle before firing while
    // still feeling responsive. This also eliminates the "need to Reset for
    // search to appear" race - fewer inflight overlaps means the abort logic
    // doesn't churn through half-completed requests.
    // iter 201: also re-fires when qFields (search scope) changes so users see
    // updated results immediately after toggling a search field in the View editor.
    const timer = setTimeout(() => fetchRows(filters, 1), 400);
    return () => clearTimeout(timer);
  }, [filters, fetchRows, productViews.qFields, sampleWarehouseScope?.id, activeCart?.id]);

  useEffect(() => {
    if (page !== 1) fetchRows(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleReset = () => setFilters(EMPTY_FILTERS);

  // iter 178h: Set Tag confirm — placed here because it depends on fetchRows
  // which is defined above but still in temporal-dead-zone earlier in the file.
  const handleConfirmSetTag = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setSavingTag(true);
    try {
      await Promise.all(Array.from(selectedIds).map((pid) =>
        api.put(`/inventory/products/${pid}`, { tag: tagValue || null })
      ));
      toast.success(t("toast.saved"));
      setSetTagOpen(false);
      setTagValue("");
      clearSelection();
      fetchRows(filters, page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || t("toast.failed_save"));
    } finally {
      setSavingTag(false);
    }
  }, [selectedIds, tagValue, clearSelection, fetchRows, filters, page, t]);

  const handleAddVariant = (row) => {
    setVariantProduct(row);
    setVariantDialogOpen(true);
  };

  const handleVariantSubmit = async (payload) => {
    try {
      await api.post(`/inventory/products/${variantProduct.product_id}/variants`, payload);
      toast.success("Variant added");
      setVariantDialogOpen(false);
      fetchRows(filters);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add variant");
    }
  };

  const handleUpdateVariant = async (entryId, payload) => {
    try {
      await api.put(`/inventory/${entryId}`, payload);
      toast.success("Variant updated");
      await fetchRows(filters);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update variant");
    }
  };

  const handleDeleteVariant = async (entryId) => {
    try {
      await api.delete(`/inventory/${entryId}`);
      toast.success("Variant deleted");
      await fetchRows(filters);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete variant");
    }
  };

  const totalQty = useMemo(() => rows.reduce((sum, r) => sum + (r.qty || 0), 0), [rows]);

  const groupedProducts = useMemo(() => {
    const map = new Map();
    const order = [];
    rows.forEach((r) => {
      if (!map.has(r.product_id)) {
        map.set(r.product_id, { product_id: r.product_id, photo_url: r.photo_url, description: r.description, package: r.package, category: r.category, subcategory: r.subcategory, product_type: r.product_type, is_bulky: r.is_bulky, available_date: r.available_date, quality_requirement: r.quality_requirement, packaging_requirement: r.packaging_requirement, customer_name: r.customer_name, entries: [] });
        order.push(r.product_id);
      }
      map.get(r.product_id).entries.push(r);
    });
    const products = order.map((id) => {
      const p = map.get(id);
      p.cbm_sum = p.entries.reduce((sum, e) => sum + (e.cbm || 0), 0);
      return p;
    });
    products.sort((a, b) => (sortDir === "asc" ? a.cbm_sum - b.cbm_sum : b.cbm_sum - a.cbm_sum));
    return products;
  }, [rows, sortDir]);

  // iter 252: only add the price rows currently visible under the active filters.
  const visibleEntryIds = (productId) => rows.filter((r) => r.product_id === productId).map((r) => r.entry_id);
  const handleCartClick = (target) => {
    if (target.product_id) addToCart(target.product_id, visibleEntryIds(target.product_id));
  };

  const openDetailByProductId = (productId) => {
    const idx = groupedProducts.findIndex((p) => p.product_id === productId);
    if (idx === -1) return;
    setDetailIndex(idx);
    setDetailOpen(true);
  };

  const handleRowClick = (row) => openDetailByProductId(row.product_id);
  const handleCardClick = (card) => openDetailByProductId(card.product_id);

  const handleDetailPrev = () => setDetailIndex((i) => Math.max(0, i - 1));
  const handleDetailNext = () => setDetailIndex((i) => Math.min(groupedProducts.length - 1, i + 1));

  const handleDetailSave = async (productId, payload) => {
    try {
      await api.put(`/inventory/products/${productId}`, payload);
      toast.success("Product saved");
      await fetchRows(filters);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const handleDetailPhotoUpload = async (productId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.put(`/inventory/products/${productId}/photo`, formData);
      toast.success("Image replaced");
      await fetchRows(filters);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Image upload failed");
    }
  };

  const handleDetailAddToCart = (productId) => addToCart(productId, visibleEntryIds(productId));

  const handleToggleBulky = async (productId, nextValue) => {
    try {
      await api.put(`/inventory/products/${productId}`, { is_bulky: nextValue });
      await fetchRows(filters);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };

  const handleRevertManual = async (row, field) => {
    try {
      const { data } = await api.post(`/inventory/products/${row.product_id}/revert-manual`, { field });
      const { description, specification_product, manual_fields, fm_values } = data[0] || {};
      setRows((rs) => rs.map((r) => (r.product_id === row.product_id ? { ...r, description, specification_product, manual_fields, fm_values } : r)));
      toast.success(t("manual_edit.reverted"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("manual_edit.revert_failed"));
    }
  };

  const handleToggleApproval = async (row) => {
    // iter 117: optimistic update - flip the row in local state IMMEDIATELY
    // and skip the full-table refetch. Previously we did `await
    // fetchRows(filters, page)` after every click, which:
    //   1. Caused the whole table to redraw + scroll to top
    //   2. Made the row appear to "jump" as user tried to click subsequent
    //      rows
    //   3. Wasted a full round trip per click
    // Now we mutate rows[] in place; the pending-count endpoint still runs
    // (small payload, no table redraw). Server response confirms the new
    // value and we sync from that in case anything else changed server-side.
    const prevApproval = row.approval;
    const nextApproval = (prevApproval || "").toLowerCase() === "yes" ? "no" : "yes";
    setRows((rs) => rs.map((r) => (r.entry_id === row.entry_id ? { ...r, approval: nextApproval } : r)));
    try {
      const { data } = await api.post(`/inventory/entries/${row.entry_id}/toggle-approval`);
      // Reconcile with server's authoritative value (in case of race with
      // another admin). Only touches this one row - no full refetch.
      setRows((rs) => rs.map((r) => (r.entry_id === row.entry_id ? { ...r, approval: data.approval } : r)));
      toast.success(`Approval set to "${data.approval}" — will sync to FileMaker within 1 minute`);
      refreshPendingCount();
    } catch (e) {
      // Roll back the optimistic flip on failure.
      setRows((rs) => rs.map((r) => (r.entry_id === row.entry_id ? { ...r, approval: prevApproval } : r)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to toggle approval");
    }
  };

  const refreshPendingCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/inventory/approvals/pending-count");
      setPendingApprovals(data.count || 0);
    } catch {
      // ignore — this is a nice-to-have counter, not critical
    }
  }, [isAdmin]);

  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  const handleFlushApprovals = async () => {
    if (!isAdmin || flushingApprovals) return;
    setFlushingApprovals(true);
    try {
      const { data } = await api.post("/inventory/approvals/flush");
      if (data.attempted === 0) {
        toast.info("No pending approval changes to sync");
      } else if (data.failed === 0) {
        toast.success(`Synced ${data.synced}/${data.attempted} approval change(s) to FileMaker`);
      } else {
        toast.warning(`Sync complete: ${data.synced} succeeded, ${data.failed} failed`);
      }
      await refreshPendingCount();
      await fetchRows(filters, page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Flush failed");
    } finally {
      setFlushingApprovals(false);
    }
  };

  const detailProduct = groupedProducts[detailIndex] || null;

  return (
    <AppLayout>
      {/* iter 206 (2026-02): Dashboard toolbar 也压到 ~50%, 与 CartPage 保持一致。
          space-y-6 → space-y-2, 标题 text-2xl → text-sm, 所有工具按钮 h-9 → h-7. */}
      {/* iter 213 (2026-02): flex-col + gap-2 + min-h 让 Dashboard 占满视口(topbar
          h-8 + main py-4 = 64px);分页栏配合 mt-auto 被推到最底部,消除"短列表
          下方大片灰色空白";长列表滚动时,分页栏 sticky bottom-0 常驻视窗底部。
          注意:必须用 `gap-2` 而不是 `space-y-2`,因为 space-y 的 `> * + *` 选择器
          特异性高于 `mt-auto`,会阻止 mt-auto 生效。 */}
      <div data-testid="dashboard-page" className="gap-2 max-w-screen-2xl flex flex-col min-h-[calc(100vh-64px)]">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-sm font-bold tracking-tight text-[#273142]" data-testid="dashboard-page-title">{t("dashboard.title")}</h1>
              {isAdmin && pendingApprovals > 0 && (
                <button
                  data-testid="flush-approvals-now"
                  onClick={handleFlushApprovals}
                  disabled={flushingApprovals}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 text-[11px] font-medium transition-colors disabled:opacity-50"
                  title="Push all pending approval changes to FileMaker now (bypass 5-minute wait)"
                >
                  <RefreshCw size={11} strokeWidth={2} className={flushingApprovals ? "animate-spin" : ""} />
                  {flushingApprovals ? "Syncing…" : `Flush ${pendingApprovals} approval${pendingApprovals === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-0" data-testid="dashboard-summary">
              {t("dashboard.total_inventory_qty")} <span className="font-semibold text-gray-900">{totalQty.toLocaleString()}</span> pcs
            </p>
          </div>

          {/* iter 88: ProductViewsBar renders in every view mode (table + grid)
              so users can switch views / configure Preset Values without first
              bouncing back to Table. FilterBar already respects lockedFields
              regardless of view mode - showing the switcher was the only piece
              missing to make presets take effect on Grid. */}
          <div className="flex-1 flex items-center justify-center">
            <ProductViewsBar
              views={productViews.views}
              currentView={productViews.currentView}
              visibleColumns={productViews.visibleColumns}
              setVisibleColumns={productViews.setVisibleColumns}
              visibleFilters={productViews.visibleFilters}
              setVisibleFilters={productViews.setVisibleFilters}
              detailView={productViews.detailView}
              setDetailView={productViews.setDetailView}
              presets={productViews.presets}
              setPresets={productViews.setPresets}
              qFields={productViews.qFields}
              setQFields={productViews.setQFields}
              categoryOptions={categoryOptions}
              applyView={productViews.applyView}
              createView={productViews.createView}
              updateCurrentView={productViews.updateCurrentView}
              renameView={productViews.renameView}
              toggleShareView={productViews.toggleShareView}
              canEditView={productViews.canEditView}
              isAdmin={productViews.isAdmin}
              deleteView={productViews.deleteView}
            />
          </div>

          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-1.5">
              <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
                <button
                  data-testid="view-toggle-table"
                  onClick={() => changeView("table")}
                  title="Table"
                  className={`w-7 h-7 flex items-center justify-center transition-colors ${view === "table" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                >
                  <Table2 size={13} strokeWidth={1.75} />
                </button>
                <button
                  data-testid="view-toggle-grid"
                  onClick={() => changeView("grid-compact")}
                  title="Compact Grid"
                  className={`w-7 h-7 flex items-center justify-center transition-colors ${view === "grid-compact" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                >
                  <LayoutGrid size={13} strokeWidth={1.75} />
                </button>
                <button
                  data-testid="view-toggle-grid-detailed"
                  onClick={() => changeView("grid-detailed")}
                  title="Detailed Grid"
                  className={`w-7 h-7 flex items-center justify-center transition-colors ${view === "grid-detailed" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                >
                  <LayoutList size={13} strokeWidth={1.75} />
                </button>
              </div>

              <div className="flex items-center border border-gray-200 rounded-md overflow-hidden" data-testid="price-currency-toggle">
                <button
                  data-testid="price-currency-usd"
                  onClick={() => setPriceCurrency("USD")}
                  className={`h-7 px-2 text-[11px] font-semibold transition-colors ${priceCurrency === "USD" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"}`}
                >
                  USD
                </button>
                <button
                  data-testid="price-currency-cad"
                  onClick={() => setPriceCurrency("CAD")}
                  className={`h-7 px-2 text-[11px] font-semibold transition-colors ${priceCurrency === "CAD" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"}`}
                >
                  CAD
                </button>
              </div>

              {/* iter 198: density toggle — only shown when table view is active */}
              {view === "table" && (
                <div className="flex items-center border border-gray-200 rounded-md overflow-hidden" data-testid="density-toggle">
                  <button
                    data-testid="density-compact"
                    onClick={() => changeDensity("compact")}
                    title="Compact (dense spreadsheet)"
                    className={`w-7 h-7 flex items-center justify-center transition-colors ${density === "compact" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                  >
                    <Rows3 size={13} strokeWidth={1.75} />
                  </button>
                  <button
                    data-testid="density-comfortable"
                    onClick={() => changeDensity("comfortable")}
                    title="Comfortable"
                    className={`w-7 h-7 flex items-center justify-center transition-colors ${density === "comfortable" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                  >
                    <Rows2 size={13} strokeWidth={1.75} />
                  </button>
                </div>
              )}

              {/* iter 207: fullscreen toggle for the products table */}
              <button
                data-testid="products-fullscreen-toggle"
                onClick={toggleFullscreen}
                title={tableFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                {tableFullscreen ? <Minimize2 size={13} strokeWidth={1.75} /> : <Maximize2 size={13} strokeWidth={1.75} />}
              </button>

              {/* iter 211 (2026-02): 会话级排序控件。选完立即刷新,不落盘 View。 */}
              <SortMenu sortKey={sortKey} sortOrder={sortOrder} changeSort={changeSort} />
            </div>
            <span className="font-mono text-[10px] text-gray-400" data-testid="data-as-of-label">
              Data as of: {dataAsOf}
            </span>
          </div>
        </div>

        <FilterBar
          filters={filters}
          setFilters={setFilters}
          onReset={handleReset}
          view={view}
          sortDir={sortDir}
          setSortDir={setSortDir}
          categoryOptions={categoryOptions}
          visibleFilters={productViews.visibleFilters}
          lockedFields={productViews.lockedFields}
          qFields={productViews.qFields}
          warehouseScope={sampleWarehouseScope}
        />

        {/* iter 123: unified selection banner - visible in EVERY view
            (List, Compact Grid, Detailed Grid) so users don't lose track
            of what they've ticked when they switch views or change pages. */}
        {selectedIds.size > 0 && (
          <div
            data-testid="global-selection-banner"
            className="flex items-center justify-between bg-gray-900 text-white rounded-lg px-3 py-1.5"
          >
            <span className="text-xs">
              <span className="font-semibold">{t("dashboard.selected_n", { n: selectedIds.size })}</span>
            </span>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="show-selected-list-button"
                onClick={() => setSelectedListOpen(true)}
                className="h-6 px-2 rounded-md bg-transparent border border-gray-600 text-white hover:bg-gray-800 text-[11px] transition-colors"
              >
                {t("dashboard.select_products")}
              </button>
              <button
                data-testid="clear-selection-button"
                onClick={clearSelection}
                className="h-6 px-2 rounded-md bg-transparent border border-gray-600 text-white hover:bg-gray-800 text-[11px] transition-colors"
              >
                {t("common.clear")}
              </button>
              {/* iter 178: single "Action" dropdown replaces the standalone
                  Add-to-Quote button. Generate Link is admin-only. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="bulk-action-menu-button"
                    className="h-6 px-2 rounded-md bg-white text-gray-900 hover:bg-gray-100 text-[11px] font-medium transition-colors flex items-center gap-1"
                  >
                    {t("dashboard.actions")} <ChevronDown size={11} strokeWidth={2} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem
                    data-testid="bulk-action-add-to-quote"
                    onSelect={() => {
                      selectedIds.forEach((id) => handleCartClick({ product_id: id }));
                      clearSelection();
                    }}
                    className="cursor-pointer"
                  >
                    <PackagePlus size={13} strokeWidth={1.75} className="mr-2" />
                    {t("dashboard.action_menu.add_to_quote")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="bulk-action-set-tag"
                    onSelect={() => { setTagValue(""); setSetTagOpen(true); }}
                    className="cursor-pointer"
                  >
                    <Tag size={13} strokeWidth={1.75} className="mr-2" />
                    {t("dashboard.action_menu.set_tag")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="bulk-action-print-label"
                    onSelect={handleBulkPrintLabel}
                    className="cursor-pointer"
                  >
                    <FileOutput size={13} strokeWidth={1.75} className="mr-2" />
                    {t("dashboard.action_menu.print_label")}
                  </DropdownMenuItem>
                  {/* iter 210 (2026-02): Export the selected products as an
                      Excel file that mirrors the currently visible columns.
                      Photos are omitted (embedding remote images requires
                      async CORS loading; can add later if needed). */}
                  <DropdownMenuItem
                    data-testid="bulk-action-export-excel"
                    onSelect={async () => {
                      try {
                        const selectedRows = rows.filter((r) => selectedIds.has(r.product_id));
                        if (!selectedRows.length) return;
                        await exportProductsExcel({
                          rows: selectedRows,
                          currency: priceCurrency,
                          filename: "products",
                        });
                        toast.success(`Exported ${selectedRows.length} rows to Excel`);
                      } catch (e) {
                        toast.error(e.message || "Export failed");
                      }
                    }}
                    className="cursor-pointer"
                  >
                    <FileSpreadsheet size={13} strokeWidth={1.75} className="mr-2" />
                    Export Excel
                  </DropdownMenuItem>
                  {canGenerateShareLink && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        data-testid="bulk-action-generate-link"
                        disabled={shareLinkGenerating}
                        onSelect={generateShareLink}
                        className="cursor-pointer"
                      >
                        <Link2 size={13} strokeWidth={1.75} className="mr-2" />
                        {shareLinkGenerating ? "…" : t("dashboard.action_menu.generate_link")}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        {view === "table" ? (
          <div
            className={tableFullscreen ? "fixed inset-0 z-50 bg-white overflow-auto p-3" : ""}
            data-testid={tableFullscreen ? "products-fullscreen-wrapper" : undefined}
          >
            {tableFullscreen && (
              <div className="flex items-center justify-between mb-2 sticky top-0 bg-white z-10 pb-2 border-b border-gray-200">
                <h2 className="font-heading text-sm font-bold text-gray-900">{t("dashboard.title")}</h2>
                <button
                  data-testid="products-fullscreen-exit"
                  onClick={toggleFullscreen}
                  className="h-7 px-2 flex items-center gap-1 rounded-md border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
                  title="Exit fullscreen (Esc)"
                >
                  <Minimize2 size={12} strokeWidth={1.75} /> Exit fullscreen
                </button>
              </div>
            )}
            <ProductTable
              rows={rows}
              totals={totals}
              density={density}
              selectedProductId={selectedProductId}
              onSelectProduct={(pid) => setSelectedProductId((cur) => cur === pid ? null : pid)}
              onAddVariant={handleAddVariant}
              onCartClick={handleCartClick}
              onRowClick={handleRowClick}
              onUpdateVariant={handleUpdateVariant}
              onDeleteVariant={handleDeleteVariant}
              onToggleBulky={handleToggleBulky}
              onToggleApproval={handleToggleApproval}
              onRevertManual={handleRevertManual}
              onLookupSimilar={(row) => {
                const target = groupedProducts.find((p) => p.product_id === row.product_id);
                setSimilarLookup({ itemNo: row.item_no, targetProduct: target });
              }}
              priceCurrency={priceCurrency}
              loading={loading}
              visibleColumns={productViews.visibleColumns}
              columnWidths={productViews.columnWidths}
              onColumnResize={productViews.setColumnWidth}
              selected={selectedIds}
              onToggleSelected={toggleSelectedId}
              onClearSelected={clearSelection}
              stickyScrollbarOffset={tableFullscreen ? 0 : 38}
              sortKey={sortKey}
              sortOrder={sortOrder}
              onSortChange={changeSort}
            />
          </div>
        ) : (
          <GridView
            products={groupedProducts}
            onAddVariant={handleAddVariant}
            onCartClick={handleCartClick}
            onCardClick={handleCardClick}
            priceCurrency={priceCurrency}
            loading={loading}
            density={view === "grid-detailed" ? "detailed" : "compact"}
            selected={selectedIds}
            onToggleSelected={toggleSelectedId}
          />
        )}

        {totalProducts > 0 && (
          /* iter 213 (2026-02): 分页栏 mt-auto 被推到 Dashboard flex 容器最底
             (短列表时贴视窗底);sticky bottom-0 让长列表滚动时也吸附视窗底部;
             ProductTable 内部 sticky 横向滚动条 bottom-[38px] 让位。全屏模式
             (fullscreen wrapper 是 fixed inset-0 z-50) 会盖住此分页栏,不冲突。 */
          <div className="mt-auto sticky bottom-0 z-30 flex items-center justify-between text-sm text-gray-600 bg-white/95 backdrop-blur border-t border-gray-200 px-3 py-1.5 rounded-b-lg shadow-[0_-2px_8px_rgba(0,0,0,0.04)]" data-testid="products-pagination">
            <span className="text-xs" data-testid="products-pagination-summary">
              Showing <span className="font-mono font-semibold text-gray-900">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalProducts)}</span> of <span className="font-mono font-semibold text-gray-900">{totalProducts}</span> products
            </span>
            <div className="flex items-center gap-2">
              {/* iter 169: user-picked page size (25 / 50 / 100). Persisted
                  to localStorage and resets to page 1 on change. */}
              <div className="flex items-center gap-1 text-xs text-gray-500 mr-2">
                <span>Per page:</span>
                {[25, 50, 100].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`products-pagination-size-${n}`}
                    onClick={() => changePageSize(n)}
                    disabled={loading}
                    className={`h-8 px-2 rounded-lg border transition-colors ${PAGE_SIZE === n ? "border-brand-500 bg-brand-50 text-brand-700 font-semibold" : "border-gray-200 bg-white hover:border-brand-300"}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <button
                data-testid="products-pagination-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="h-8 px-3 rounded-lg border border-gray-200 bg-white hover:border-brand-300 disabled:opacity-40 disabled:pointer-events-none transition-colors text-xs"
              >
                ← Prev
              </button>
              <span className="text-xs text-gray-500 font-mono" data-testid="products-pagination-current">
                Page {page} / {Math.max(1, Math.ceil(totalProducts / PAGE_SIZE))}
              </span>
              <button
                data-testid="products-pagination-next"
                onClick={() => setPage((p) => (p * PAGE_SIZE < totalProducts ? p + 1 : p))}
                disabled={page * PAGE_SIZE >= totalProducts || loading}
                className="h-8 px-3 rounded-lg border border-gray-200 bg-white hover:border-brand-300 disabled:opacity-40 disabled:pointer-events-none transition-colors text-xs"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      <VariantFormDialog
        open={variantDialogOpen}
        onOpenChange={setVariantDialogOpen}
        product={variantProduct}
        onSubmit={handleVariantSubmit}
      />

      <ProductDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        product={detailProduct}
        hasPrev={detailIndex > 0}
        hasNext={detailIndex < groupedProducts.length - 1}
        onPrev={handleDetailPrev}
        onNext={handleDetailNext}
        onSave={handleDetailSave}
        onPhotoUpload={handleDetailPhotoUpload}
        onAddToCart={handleDetailAddToCart}
        variant={productViews.detailView}
        isAdmin={isAdmin}
        canEditMaster={canEditMaster}
        onToggleApproval={handleToggleApproval}
      />

      <SimilarItemsDialog
        open={!!similarLookup}
        onOpenChange={(v) => { if (!v) setSimilarLookup(null); }}
        itemNo={similarLookup?.itemNo || ""}
        targetProduct={similarLookup?.targetProduct || null}
        onImported={() => { setSimilarLookup(null); fetchRows(filters, page); }}
      />

      {/* iter 123: "已选清单" dialog - lists every product ticked across
          pages and views so users can review before adding to a quote. */}
      <Dialog open={selectedListOpen} onOpenChange={setSelectedListOpen}>
        <DialogContent className="rounded-xl sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="selected-list-dialog">
          <DialogHeader>
            <DialogTitle>{t("dashboard.selected_products")} ({selectedIds.size})</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {selectedIds.size === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">{t("dashboard.no_selection")}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {[...selectedIds].map((id) => {
                  const meta = selectedMeta[id] || {};
                  return (
                    <li key={id} className="py-2.5 flex items-start justify-between gap-3" data-testid={`selected-list-item-${id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs font-bold text-gray-900">{meta.item_no || id}</div>
                        <div className="text-xs text-gray-500 truncate">{meta.description || "-"}</div>
                        {meta.contract_no && (
                          <div className="text-[10px] text-gray-400 font-mono mt-0.5">{t("dashboard.columns_labels.contract_no")} {meta.contract_no}</div>
                        )}
                      </div>
                      <button
                        data-testid={`selected-list-remove-${id}`}
                        onClick={() => toggleSelectedId(id, null)}
                        className="text-xs text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                      >
                        {t("common.remove")}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="selected-list-clear-all">
              {t("common.clear_all")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                selectedIds.forEach((id) => handleCartClick({ product_id: id }));
                clearSelection();
                setSelectedListOpen(false);
              }}
              disabled={selectedIds.size === 0}
              data-testid="selected-list-add-all-to-cart"
            >
              {t("dashboard.add_all_to_quote")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shareLinkDialogOpen} onOpenChange={setShareLinkDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="share-link-dialog">
          <DialogHeader>
            <DialogTitle>{t("dashboard.share_link_title")}</DialogTitle>
          </DialogHeader>
          {shareLinkInfo ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                {shareLinkInfo.product_count} · {t("dashboard.expires_on")} {new Date(shareLinkInfo.expires_at).toLocaleDateString()}.
                {" "}{t("dashboard.share_link_note")}
              </p>
              <div className="flex items-center gap-2">
                <input
                  data-testid="share-link-url"
                  readOnly
                  value={shareLinkInfo.url}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 h-9 px-3 rounded-lg border border-gray-300 bg-gray-50 font-mono text-xs text-gray-700"
                />
                <Button data-testid="share-link-copy" size="sm" onClick={copyShareLink}>
                  <Copy size={13} strokeWidth={1.75} className="mr-1.5" /> {t("common.copy")}
                </Button>
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => setShareLinkDialogOpen(false)} data-testid="share-link-close">
                  {t("common.close")}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={setTagOpen} onOpenChange={setSetTagOpen}>
        <DialogContent className="max-w-md" data-testid="set-tag-dialog">
          <DialogHeader>
            <DialogTitle>{t("dashboard.set_tag_title", { n: selectedIds.size })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">{t("dashboard.set_tag_hint")}</p>
            <input
              data-testid="set-tag-input"
              type="text"
              value={tagValue}
              onChange={(e) => setTagValue(e.target.value)}
              placeholder=""
              className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm"
              autoFocus
            />
            {myTags.length > 0 && (
              <div data-testid="my-tags-chip-row" className="pt-1">
                <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">{t("dashboard.your_tags")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {myTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      data-testid={`my-tag-chip-${tag}`}
                      onClick={() => setTagValue(tag)}
                      className={`h-6 px-2 rounded-full border text-[11px] font-medium transition-colors ${tagValue === tag ? "bg-brand-50 border-brand-300 text-brand-700" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setSetTagOpen(false)} data-testid="set-tag-cancel" disabled={savingTag}>
                {t("common.cancel")}
              </Button>
              <Button size="sm" onClick={handleConfirmSetTag} data-testid="set-tag-confirm" disabled={savingTag}>
                {savingTag ? t("common.saving") : t("common.apply")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
